/**
 * SiteVerdict — Layer 1 of the restructured analysis panel.
 *
 * Always-visible headline that gives a non-technical reader the answer
 * to "is this site good or bad?" in 5 seconds:
 *
 *   [Score]   STATUS LABEL
 *             One-line summary
 *
 * Color-coded border + score so a quick glance carries meaning even
 * before reading. Status is derived from:
 *   - readiness_score (computed backend-side from 9 themes)
 *   - any hard-stop fields (flood / quick clay / salmon / NiN A / heritage)
 *   - concerns array severity
 *
 * If any hard-stop fires the site is NOT_SUITABLE regardless of score.
 * If readiness is high but there's at least one high-severity concern,
 * the site is capped at CONDITIONAL.
 */

import React from 'react';
import { tierForIndex, SeidrIndexExplainer } from './SeidrIndex';

function deriveVerdict(site, useCase = 'hydrogen') {
  // Hard stops: only the genuinely show-stopping flags. Backend
  // explicitly does NOT treat kvikkleire as a hard exclude (see
  // sites.py kvikkleire comment) — it's surfaced for caution and
  // handled as a soft concern, not a blocker.
  const hardStops = [
    site.flood_hard_stop || site.flood_zone,
    site.is_salmon_river || site.has_salmon,
    site.nin_type_a,
    site.har_kulturminne,
  ].filter(Boolean);

  const concerns = Array.isArray(site.concerns) ? site.concerns : [];
  const highConcerns   = concerns.filter(c => c.severity === 'high');
  const mediumConcerns = concerns.filter(c => c.severity === 'medium');

  const score = site.readiness_score != null ? Math.round(site.readiness_score) : null;

  // Hard stops override the SEIDR Index tier entirely — they're
  // categorical blockers, not a low score.
  if (hardStops.length > 0) {
    return { status: 'Hard blocker', color: '#dc2626', score,
             summary: `Hard blocker: ${hardStops[0]}` };
  }
  // High-severity concerns cap the tier at Conditional regardless of
  // what the underlying themes scored.
  if (highConcerns.length > 0) {
    return {
      status: 'Conditional', color: '#d97706', score,
      summary: highConcerns.length === 1
        ? `1 high-severity concern: ${highConcerns[0].message.split(' — ')[0]}`
        : `${highConcerns.length} high-severity concerns identified`,
    };
  }
  if (score == null) {
    // The SEIDR Index (readiness_score) is computed server-side for
    // data-center searches only — its theme weights are tuned for DCs.
    // A hydrogen / ammonia parcel that came out of /sites/search has
    // been fully screened; it just has no index. Say so instead of
    // "Awaiting Analysis", which would never resolve.
    if (useCase !== 'data_center' && site._from_search) {
      return {
        status: 'Screened', color: '#2563eb', score: null,
        summary: mediumConcerns.length > 0
          ? `${mediumConcerns.length} concern${mediumConcerns.length === 1 ? '' : 's'} to address · SEIDR Index applies to data-center sites only`
          : 'No blockers identified across screened criteria · SEIDR Index applies to data-center sites only',
      };
    }
    return { status: 'Awaiting Analysis', color: '#6b7280', score: null,
             summary: 'Enrichment in progress' };
  }
  // Tier from the shared SEIDR Index module — Strong / Suitable /
  // Conditional / Weak, same mapping as Compare.
  const { tier, color } = tierForIndex(score);
  const note = mediumConcerns.length > 0
    ? `${mediumConcerns.length} concern${mediumConcerns.length === 1 ? '' : 's'} ${score >= 75 ? 'identified' : 'to address'}`
    : (score >= 75
        ? 'No material concerns identified across measured themes'
        : 'Multiple themes below threshold');
  return { status: tier, color, score, summary: note };
}

export default function SiteVerdict({ site, useCase = 'hydrogen' }) {
  if (!site) return null;
  const v = deriveVerdict(site, useCase);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16,
      padding: '14px 18px',
      borderLeft: `4px solid ${v.color}`,
      borderBottom: '1px solid #1f2937',
      background: 'linear-gradient(90deg, rgba(255,255,255,0.02) 0%, transparent 100%)',
    }}>
      {/* SEIDR Index block */}
      <div style={{ minWidth: 92, textAlign: 'center' }}>
        {v.score != null ? (
          <>
            <div className="numeric" style={{ fontSize: 32, fontWeight: 600, color: v.color, lineHeight: 1 }}>
              {v.score}
            </div>
            <div className="section-header" style={{ marginTop: 6, borderBottom: 0, padding: 0 }}>
              SEIDR Index
            </div>
            <div style={{ marginTop: 4 }}>
              <SeidrIndexExplainer />
            </div>
          </>
        ) : (
          <div style={{ fontSize: 14, color: '#94a3b8' }}>—</div>
        )}
      </div>
      {/* Status + summary */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: v.color, marginBottom: 4 }}>
          {v.status}
        </div>
        <div style={{ fontSize: 13, opacity: 0.85 }}>
          {v.summary}
        </div>
      </div>
    </div>
  );
}
